import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";

import { auth } from "@/lib/firebase";

import { ProviderSummary } from "@/types/provider";
import { TenantSelect } from "@/types/tenant";

import { requestData } from "@/api/api-config";

export async function fetchUserData(
  uid: string,
  tenant?: string,
): Promise<ProviderSummary | null> {
  // Don't catch errors - let them propagate
  // The afterResponse hook in api-config.ts handles IP blocking errors
  return await requestData<ProviderSummary>(
    `auth/get-user-from-firebase-uid/${uid}`,
    "get",
    {
      headers: tenant
        ? {
            TENANT: tenant,
          }
        : {},
    },
    false, // Don't catch errors - let them propagate
  );
}

/**
 * Exchange the Microsoft ID token from an "account already exists" refusal for a sign-in to
 * that account. The API checks Microsoft's signature and only answers when Microsoft vouches
 * for the email's domain (`xms_edov`) and the account's clinic connected that organization.
 */
export async function signInWithVerifiedMicrosoftEmail(idToken: string) {
  return await requestData<{ customToken: string }>(
    "auth/microsoft/verified-email",
    "post",
    { json: { idToken } },
    false,
  );
}

export async function resetPassword(
  email: string,
  options?: { tenant?: string },
) {
  return await requestData<void>(
    `auth/reset-password`,
    "post",
    { json: { email, tenant: options?.tenant } },
    false,
  );
}

export const login = async (
  email: string,
  password: string,
): Promise<any | null> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    return userCredential.user;
  } catch (error: any) {
    console.error("Error signing in: ", error);
    throw new Error("Invalid email or password");
  }
};

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    await signInWithPopup(auth, provider);
  } catch (error: any) {
    if (error.code === "auth/operation-not-allowed") {
      return "Google signin is not currently configured for your tenant. Please reach out to the Decoda Engineering team to set it up for you.";
      // https://console.cloud.google.com/customer-identity/providers?project=decoda-397301
      // Make sure the tenant has google sign in enabled __IN THE SCOPE__, configured with the correct web client id and web client secret.
      // see other tenants to configure the same id + secret
    } else {
      console.error("Error signing in with Google: ", error);
      return "An error occurred while signing in with Google";
    }
  }
}

let tenantsCache: TenantSelect[] | null = null;
let firebaseUserUidCache: string | null = null;
let tenantsCacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export async function getAvailableTenants(
  firebaseUserUid: string,
): Promise<TenantSelect[] | null> {
  if (
    tenantsCache &&
    tenantsCacheTimestamp &&
    Date.now() - tenantsCacheTimestamp < CACHE_DURATION &&
    firebaseUserUidCache === firebaseUserUid
  ) {
    return tenantsCache;
  }
  const tenants = await requestData<TenantSelect[]>("tenant/select", "get");
  tenantsCache = tenants;
  tenantsCacheTimestamp = Date.now();
  firebaseUserUidCache = firebaseUserUid;
  return tenants;
}

export async function setSelectedTenant(
  firebaseUserUid: string,
  tenantName: string,
  catchError = true,
): Promise<boolean | null> {
  return await requestData<boolean>(
    "auth/set-selected-tenant",
    "post",
    { json: { firebaseUserUid, tenantName } },
    catchError,
  );
}
